import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, CheckCircle2, Circle, Lock, PlayCircle, Clock, Loader2, Download, ExternalLink, GraduationCap, VideoOff,
  ClipboardCheck, Award, User, FileText,
} from 'lucide-react';
import { EmptyState, PageSkeleton } from '@/components/dashboard/MetricCard';
import QuizPanel from '@/components/academy/QuizPanel';
import { academyApi, fmtMinutes, blockParts } from '@/services/academyApi';
import { useDashboard } from '@/context/DashboardContext';

/*
  Reproductor de FHC Academy (/dashboard/academia/:courseId?leccion=<id> | ?quiz=<id del bloque|final>).
  Cada módulo del curso es un «bloque» (Bloque 0 abierto a todos; ver DashboardAcademia).
  El servidor decide qué está abierto (plan de cada bloque, quiz del bloque anterior aprobado) y no envía
  vídeo ni recursos de lo bloqueado; aquí solo se pinta. Recorrido: clases del bloque → quiz → siguiente bloque
  → … → evaluación final → diploma.
*/

const IFRAME_ALLOW = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
const RANK = { starter: 1, basica: 2, premium: 3 };
const FINAL = 'final';
// Tamaño legible: KB hasta 1.023 KB y MB (coma decimal) a partir de 1.024 KB
const fmtSize = (bytes) => {
  const kb = Math.max(1, Math.round(bytes / 1024));
  if (kb < 1024) return `${kb} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

function VideoPlayer({ lesson }) {
  if (!lesson.embed_url) {
    return (
      <div className="relative w-full aspect-video rounded-2xl border border-white/[0.06] bg-gradient-to-br from-[#0d1829] to-[#060d1b] flex flex-col items-center justify-center gap-2 px-6 text-center text-gray-400"
        data-testid="video-soon">
        <VideoOff size={28} className="text-[#c9a84c]/70" />
        <p className="text-sm text-white font-medium">Vídeo próximamente</p>
        <p className="text-xs max-w-sm">Estamos grabando esta clase. Mientras tanto tienes su objetivo debajo y puedes seguir avanzando en el bloque.</p>
      </div>
    );
  }
  return (
    <div className="relative w-full aspect-video overflow-hidden rounded-2xl border border-white/[0.06] bg-black">
      <iframe key={lesson.id} src={lesson.embed_url} title={`Vídeo: ${lesson.title}`} allow={IFRAME_ALLOW} allowFullScreen loading="lazy"
        referrerPolicy="strict-origin-when-cross-origin" className="absolute inset-0 h-full w-full" />
    </div>
  );
}

function LockedPlayer({ course, membership, planName, planId, sequence, prevQuiz, onGoQuiz }) {
  if (sequence) {
    return (
      <div className="relative w-full aspect-video overflow-hidden rounded-2xl border border-[#c9a84c]/20 bg-[#0d1829]" data-testid="lesson-locked-sequence">
        <div className="relative flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="w-12 h-12 rounded-full bg-[#c9a84c]/15 flex items-center justify-center"><Lock size={20} className="text-[#c9a84c]" /></span>
          <p className="text-white font-semibold">Este bloque se abre al aprobar el quiz anterior</p>
          {prevQuiz && <p className="text-sm text-gray-300 max-w-sm hidden sm:block">Completa el quiz de «{prevQuiz.title}» para seguir.</p>}
          {prevQuiz && (
            <button type="button" onClick={() => onGoQuiz(prevQuiz.id)} className="inline-flex items-center gap-2 rounded-xl bg-[#c9a84c] px-4 py-2.5 text-sm font-semibold text-[#0a1628] hover:bg-[#d6b75a]">
              Ir al quiz <ArrowRight size={15} />
            </button>
          )}
        </div>
      </div>
    );
  }
  // viewer_member lo calcula el servidor con la membresía efectiva; el contexto es el respaldo
  const current = membership?.active_membership;
  const isMember = typeof course.viewer_member === 'boolean' ? course.viewer_member : !!current;
  const needs = planName || course.upgrade_plan_name || course.min_plan_name;
  const upgrade = isMember && (typeof course.viewer_member === 'boolean' || RANK[current] < (RANK[planId || course.min_plan] || 0));
  return (
    <div className="relative w-full aspect-video overflow-hidden rounded-2xl border border-[#c9a84c]/20 bg-[#0d1829]" data-testid="lesson-locked">
      {course.cover_url && <img src={course.cover_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25 blur-sm" />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#060d1b] via-[#060d1b]/70 to-[#060d1b]/40" />
      <div className="relative flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <span className="w-12 h-12 rounded-full bg-[#c9a84c]/15 flex items-center justify-center"><Lock size={20} className="text-[#c9a84c]" /></span>
        <p className="text-white font-semibold">{needs ? `Incluido en la membresía ${needs}` : 'Contenido para miembros del club'}</p>
        <p className="text-sm text-gray-300 max-w-sm hidden sm:block">
          {upgrade ? `Tu membresía actual no incluye este bloque. Pasa a ${needs} para verlo completo.` : `Hazte miembro${needs ? ` ${needs}` : ''} para acceder a este contenido.`}
        </p>
        <Link to="/dashboard#membresias" className="inline-flex min-h-[40px] items-center gap-2 rounded-xl bg-[#c9a84c] px-4 py-2.5 text-sm font-semibold text-[#0a1628] hover:bg-[#d6b75a] transition-colors" data-testid="lesson-locked-cta">
          {upgrade ? `Mejorar a ${needs}` : 'Hazte miembro'} <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  );
}

function LessonRow({ lesson, index, active, showFree, onSelect }) {
  const Icon = lesson.completed ? CheckCircle2 : lesson.locked ? Lock : active ? PlayCircle : Circle;
  const iconCls = lesson.completed ? 'text-emerald-400' : active ? 'text-[#c9a84c]' : 'text-gray-600';
  return (
    <button type="button" onClick={() => onSelect(lesson.id)} aria-current={active ? 'true' : undefined}
      className={`w-full flex items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${active ? 'bg-[#c9a84c]/10' : 'hover:bg-white/[0.03]'}`}
      data-testid={`lesson-row-${lesson.id}`}>
      <Icon size={16} className={`mt-0.5 flex-shrink-0 ${iconCls}`} />
      <span className="flex-1 min-w-0">
        <span className={`block text-sm leading-snug ${active ? 'text-white font-medium' : lesson.locked ? 'text-gray-500' : 'text-gray-300'}`}>
          {index}. {lesson.title}
        </span>
        <span className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500">
          {lesson.duration_min > 0 && <span>{fmtMinutes(lesson.duration_min)}</span>}
          {!lesson.locked && !lesson.has_video && <span className="text-gray-500">Vídeo próximamente</span>}
          {showFree && lesson.free_preview && <span className="font-semibold text-emerald-300">Vista previa gratis</span>}
        </span>
      </span>
    </button>
  );
}

function QuizRow({ label, info, active, onSelect, testid, icon: Icon = ClipboardCheck }) {
  const passed = info.passed;
  const StateIcon = passed ? CheckCircle2 : info.available ? Icon : Lock;
  return (
    <button type="button" onClick={onSelect} aria-current={active ? 'true' : undefined} data-testid={testid}
      className={`w-full flex items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${active ? 'bg-[#c9a84c]/10' : 'hover:bg-white/[0.03]'}`}>
      <StateIcon size={16} className={`mt-0.5 flex-shrink-0 ${passed ? 'text-emerald-400' : info.available ? 'text-[#c9a84c]' : 'text-gray-600'}`} />
      <span className="flex-1 min-w-0">
        <span className={`block text-sm leading-snug font-medium ${info.available || passed ? 'text-[#e3c87a]' : 'text-gray-500'}`}>{label}</span>
        <span className="mt-0.5 block text-[11px] text-gray-500">
          {passed ? `Aprobado · ${info.best_pct} %` : `${info.question_count} preguntas${info.attempts ? ` · mejor nota ${info.best_pct} %` : ''}`}
        </span>
      </span>
    </button>
  );
}

const BLOCKED_TEXT = {
  lessons: 'Completa antes las clases con vídeo de este bloque y el quiz se abrirá.',
  sequence: 'Aprueba primero el quiz del bloque anterior.',
  modules: 'Aprueba primero los quizzes de todos los bloques.',
  plan: 'Este quiz es para socios con la membresía necesaria.',
};

export default function DashboardCurso() {
  const { courseId } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { membership } = useDashboard();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastCert, setLastCert] = useState(null);
  const topRef = useRef(null);
  const viewed = useRef(new Set());

  const load = useCallback(() => academyApi.course(courseId).then(setCourse).catch((e) => setError(e.message)), [courseId]);
  useEffect(() => { setCourse(null); setError(null); load(); }, [load]);

  // Recorrido completo: clases + quiz de cada módulo + evaluación final
  const steps = useMemo(() => {
    if (!course) return [];
    const out = [];
    course.modules.forEach((m, mi) => {
      m.lessons.forEach((l) => out.push({ kind: 'lesson', id: l.id, lesson: { ...l, module_id: m.id, module_title: m.title, module_index: mi }, module: m, mi }));
      if (m.quiz) out.push({ kind: 'quiz', id: m.id, module: m, mi });
    });
    if (course.final_quiz) out.push({ kind: 'final', id: FINAL });
    return out;
  }, [course]);
  const flat = useMemo(() => steps.filter((s) => s.kind === 'lesson').map((s) => s.lesson), [steps]);

  const requestedQuiz = params.get('quiz');
  const requestedLesson = params.get('leccion');
  let current = null;
  if (requestedQuiz) current = steps.find((s) => s.kind !== 'lesson' && s.id === requestedQuiz) || null;
  if (!current) {
    current = steps.find((s) => s.kind === 'lesson' && s.id === requestedLesson)
      || steps.find((s) => s.kind === 'lesson' && s.id === course?.next_lesson_id) || steps[0] || null;
  }
  const idx = current ? steps.indexOf(current) : -1;
  const prev = idx > 0 ? steps[idx - 1] : null;
  const next = idx >= 0 && idx < steps.length - 1 ? steps[idx + 1] : null;
  const lesson = current?.kind === 'lesson' ? current.lesson : null;

  useEffect(() => {
    if (!lesson || lesson.locked || viewed.current.has(lesson.id)) return;
    viewed.current.add(lesson.id);
    academyApi.lessonViewed(lesson.id).catch(() => {});
  }, [lesson]);

  const goStep = useCallback((step) => {
    if (!step) return;
    setParams(step.kind === 'lesson' ? { leccion: step.id } : { quiz: step.id });
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [setParams]);
  const select = useCallback((lessonId) => goStep({ kind: 'lesson', id: lessonId }), [goStep]);
  const goQuiz = useCallback((quizId) => goStep({ kind: 'quiz', id: quizId }), [goStep]);

  const [downloading, setDownloading] = useState(false);
  const downloadWorkbook = async () => {
    const wb = current?.module?.workbook;
    if (!wb) return;
    setDownloading(true);
    try { await academyApi.downloadWorkbook(course.id, current.module.id, wb.filename || 'cuaderno.pdf'); } catch (e) { toast.error(e.message); } finally { setDownloading(false); }
  };

  const toggleComplete = async () => {
    if (!lesson || lesson.locked) return;
    const completed = !lesson.completed;
    setSaving(true);
    try {
      const r = await academyApi.completeLesson(lesson.id, completed);
      await load();
      if (!completed) { toast('Clase marcada como pendiente'); return; }
      toast.success(r.progress_pct >= 100 ? 'Curso completado. Enhorabuena.' : 'Clase completada');
      if (next && (next.kind !== 'lesson' || !next.lesson.locked)) setTimeout(() => goStep(next), 600);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Tras aprobar: siguiente módulo, evaluación final o diploma
  const afterPass = (step) => {
    if (step.kind === 'final') return null;
    const nextModule = course.modules[step.mi + 1];
    if (nextModule?.lessons?.length) return { label: `Empezar el bloque ${blockParts(nextModule.title, step.mi + 1).number}`, go: () => select(nextModule.lessons[0].id) };
    if (course.final_quiz) return { label: 'Ir a la evaluación final', go: () => goQuiz(FINAL) };
    return null;
  };

  if (error) {
    return (
      <div className="space-y-6">
        <Link to="/dashboard/academia" className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-white"><ArrowLeft size={15} /> Academia</Link>
        <EmptyState icon={GraduationCap} title="No hemos encontrado este curso" text={error}
          action={<button type="button" onClick={() => navigate('/dashboard/academia')} className="rounded-xl bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628]">Ver todos los cursos</button>} />
      </div>
    );
  }
  if (!course) return <PageSkeleton cards={0} rows={6} />;

  const prevQuizFor = (mi) => {
    for (let i = mi - 1; i >= 0; i -= 1) if (course.modules[i].quiz) return course.modules[i];
    return null;
  };
  const quizInfo = current?.kind === 'quiz' ? current.module.quiz : current?.kind === 'final' ? course.final_quiz : null;
  const nav = (
    <div className="flex items-center justify-between gap-2 border-t border-white/[0.05] pt-4">
      <button type="button" onClick={() => goStep(prev)} disabled={!prev}
        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400">
        <ArrowLeft size={15} /> Anterior
      </button>
      <button type="button" onClick={() => goStep(next)} disabled={!next} data-testid="step-next"
        className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-sm text-white hover:bg-white/[0.05] disabled:opacity-30">
        {next?.kind === 'quiz' ? 'Ir al quiz del bloque' : next?.kind === 'final' ? 'Evaluación final' : next ? 'Siguiente clase' : 'Siguiente'} <ArrowRight size={15} />
      </button>
    </div>
  );

  let n = 0;
  return (
    <div className="space-y-5" data-testid="dashboard-curso" ref={topRef}>
      <div className="flex flex-col gap-2">
        <Link to="/dashboard/academia" className="inline-flex w-fit items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors">
          <ArrowLeft size={15} /> Academia
        </Link>
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{course.title}</h2>
          {course.subtitle && <p className="text-sm text-gray-400 mt-1">{course.subtitle}</p>}
        </div>
        {course.certificate && (
          <Link to={`/dashboard/academia/diploma/${encodeURIComponent(course.certificate.code)}`} data-testid="course-diploma-link"
            className="inline-flex w-fit items-center gap-2 rounded-xl border border-[#c9a84c]/30 bg-[#c9a84c]/10 px-3 py-2 text-sm font-medium text-[#e3c87a] hover:bg-[#c9a84c]/15">
            <Award size={15} /> Ver mi diploma
          </Link>
        )}
      </div>

      {steps.length === 0 ? (
        <EmptyState icon={GraduationCap} title="Este curso aún no tiene clases" text="Vuelve pronto: el equipo está preparando el contenido." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start">
          <div className="space-y-4 min-w-0">
            {lesson && (
              <>
                {lesson.locked
                  ? <LockedPlayer course={course} membership={membership} planName={current.module.min_plan_name} planId={current.module.min_plan}
                      sequence={lesson.lock_reason === 'sequence'} prevQuiz={prevQuizFor(current.mi)} onGoQuiz={goQuiz} />
                  : <VideoPlayer lesson={lesson} />}
                <motion.div key={lesson.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}
                  className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">{lesson.module_title}</p>
                      <h3 className="text-white font-semibold text-lg leading-snug mt-0.5">{lesson.title}</h3>
                      <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-400">
                        <span>Clase {flat.findIndex((l) => l.id === lesson.id) + 1} de {flat.length}</span>
                        {lesson.duration_min > 0 && <span className="inline-flex items-center gap-1"><Clock size={12} /> {fmtMinutes(lesson.duration_min)}</span>}
                        {lesson.speaker && <span className="inline-flex items-center gap-1"><User size={12} /> {lesson.speaker}</span>}
                        {lesson.free_preview && course.locked && <span className="font-semibold text-emerald-300">Vista previa gratis</span>}
                      </p>
                    </div>
                    {!lesson.locked && (
                      <button type="button" onClick={toggleComplete} disabled={saving} data-testid="lesson-complete"
                        className={`inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${lesson.completed
                          ? 'border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15'
                          : 'bg-[#c9a84c] text-[#0a1628] hover:bg-[#d6b75a]'}`}>
                        {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                        {lesson.completed ? 'Completada' : 'Marcar como completada'}
                      </button>
                    )}
                  </div>
                  {!lesson.locked && lesson.description && (
                    <div>
                      <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1">Qué trabajarás en esta clase</p>
                      <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{lesson.description}</p>
                    </div>
                  )}
                  {!lesson.locked && lesson.resources?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">Recursos de la clase</p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {lesson.resources.map((r, i) => {
                          const isFile = /\.(pdf|zip|docx?|xlsx?|pptx?|png|jpe?g|mp4)(\?|$)/i.test(r.url);
                          const Icon = isFile ? Download : ExternalLink;
                          return (
                            <a key={`${r.url}-${i}`} href={r.url} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-sm text-gray-300 hover:border-[#c9a84c]/25 hover:text-white transition-colors">
                              <Icon size={15} className="flex-shrink-0 text-[#c9a84c]" />
                              <span className="truncate">{r.title}</span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {!lesson.locked && current.module.workbook?.available && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-[#c9a84c]/20 bg-[#c9a84c]/[0.05] p-3.5" data-testid="workbook-box">
                      <span className="w-10 h-10 flex-shrink-0 rounded-lg bg-[#c9a84c]/15 flex items-center justify-center"><FileText size={18} className="text-[#c9a84c]" /></span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium">{current.module.workbook.title}</p>
                        <p className="text-xs text-gray-400">PDF para completar durante las clases del bloque{current.module.workbook.size ? ` · ${fmtSize(current.module.workbook.size)}` : ''}</p>
                      </div>
                      <button type="button" onClick={downloadWorkbook} disabled={downloading} data-testid="workbook-download"
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#c9a84c] px-4 py-2.5 text-sm font-semibold text-[#0a1628] hover:bg-[#d6b75a] disabled:opacity-60">
                        {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Descargar cuaderno
                      </button>
                    </div>
                  )}
                  {nav}
                </motion.div>
              </>
            )}

            {quizInfo && (
              quizInfo.available || quizInfo.passed ? (
                <>
                  <QuizPanel key={current.id} courseId={course.id} quizId={current.id} isFinal={current.kind === 'final'}
                    onPassed={(r) => { if (r.certificate) setLastCert(r.certificate); load(); }}
                    onNext={current.kind === 'final'
                      ? ((lastCert || course.certificate) ? () => navigate(`/dashboard/academia/diploma/${encodeURIComponent((lastCert || course.certificate).code)}`) : null)
                      : afterPass(current)?.go}
                    nextLabel={current.kind === 'final' ? 'Ver mi diploma' : afterPass(current)?.label} />
                  <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">{nav}</div>
                </>
              ) : (
                <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4" data-testid="quiz-blocked">
                  <div className="flex items-start gap-3">
                    <span className="w-10 h-10 flex-shrink-0 rounded-full bg-white/[0.04] flex items-center justify-center"><Lock size={18} className="text-gray-400" /></span>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">{current.kind === 'final' ? 'Evaluación final' : 'Quiz del bloque'}</p>
                      <h3 className="text-white font-semibold">{current.kind === 'final' ? 'Evaluación final del curso' : current.module.title}</h3>
                      <p className="text-sm text-gray-400 mt-1">{BLOCKED_TEXT[quizInfo.blocked_reason] || 'Todavía no está disponible.'}</p>
                      {quizInfo.blocked_reason === 'plan' && (
                        <Link to="/dashboard#membresias" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628]">Ver membresías <ArrowRight size={14} /></Link>
                      )}
                    </div>
                  </div>
                  {nav}
                </div>
              )
            )}
          </div>

          {/* Temario: debajo del reproductor en móvil, columna lateral en escritorio */}
          <aside className="rounded-2xl border border-white/[0.06] bg-white/[0.02] lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
            <div className="p-4 border-b border-white/[0.06] space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-gray-400">
                  {course.completed_lessons} de {course.total_lessons} clases{course.quizzes_total ? ` · ${course.quizzes_passed} de ${course.quizzes_total} quizzes` : ''}
                </span>
                <span className="text-[#c9a84c] font-medium tabular-nums">{course.progress_pct} %</span>
              </div>
              <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#c9a84c] to-[#dbb85e] rounded-full transition-[width] duration-500" style={{ width: `${course.progress_pct}%` }} />
              </div>
              {(course.locked || course.plan_locked_modules > 0) && (course.upgrade_plan_name || course.min_plan_name) && (
                <Link to="/dashboard#membresias" data-testid="course-upgrade-cta"
                  className="mt-1 flex min-h-[40px] items-center gap-2 rounded-xl border border-[#c9a84c]/25 bg-[#c9a84c]/[0.07] px-3 py-2 text-xs text-[#c9a84c] hover:bg-[#c9a84c]/[0.12]">
                  <Lock size={13} className="flex-shrink-0" />
                  <span>
                    {!course.viewer_member
                      ? 'Hazte miembro para seguir con el resto de bloques'
                      : `Mejora a ${course.upgrade_plan_name || course.min_plan_name} para abrir ${course.plan_locked_modules === 1 ? 'el bloque que falta' : `los ${course.plan_locked_modules} bloques que faltan`}`}
                  </span>
                </Link>
              )}
            </div>
            <nav aria-label="Temario del curso" className="p-2 space-y-3">
              {course.modules.map((m, mi) => (
                <div key={m.id} data-testid={`module-${mi + 1}`}>
                  <p className="px-3 pt-2 pb-1 flex items-start gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-gray-500">
                    {m.locked && <Lock size={11} className="mt-0.5 flex-shrink-0" />}
                    <span>{m.title}{m.locked && m.lock_reason === 'plan' && m.min_plan_name ? ` · ${m.min_plan_name}` : ''}</span>
                  </p>
                  {m.lessons.map((l) => {
                    n += 1;
                    return <LessonRow key={l.id} lesson={l} index={n} active={lesson?.id === l.id} showFree={course.locked} onSelect={select} />;
                  })}
                  {m.quiz && (
                    <QuizRow label="Quiz del bloque" info={m.quiz} active={current?.kind === 'quiz' && current.id === m.id}
                      onSelect={() => goQuiz(m.id)} testid={`quiz-row-${mi + 1}`} />
                  )}
                </div>
              ))}
              {course.final_quiz && (
                <div className="border-t border-white/[0.06] pt-2">
                  <QuizRow label="Evaluación final" info={course.final_quiz} active={current?.kind === 'final'} icon={Award}
                    onSelect={() => goQuiz(FINAL)} testid="quiz-row-final" />
                  {course.certificate && (
                    <Link to={`/dashboard/academia/diploma/${encodeURIComponent(course.certificate.code)}`}
                      className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-[#e3c87a] hover:bg-white/[0.03]">
                      <Award size={16} className="text-[#c9a84c]" /> Tu diploma
                    </Link>
                  )}
                </div>
              )}
            </nav>
          </aside>
        </div>
      )}
    </div>
  );
}
